import { contributionTypes } from './constants';

export const formatAmount = (amount: any) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  if (!amount) {
    return formatter.format(Number(0));
  }
  return formatter.format(Number(amount));
};

export const formatAmountWithoutDecimals = (amount: any) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
  if (!amount) {
    return formatter.format(Number(0));
  }
  return formatter.format(Number(amount));
};

export const formatAmountWithoutDollar = (amount: any) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  if (!amount) {
    return formatter.format(Number(0)).replace('$', '');
  }
  const num = amount.toString().replace(/,/g, '');

  return formatter.format(Number(num)).replace('$', '');
};

export const formatAmountWithoutDollarAndDecimals = (amount: any) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
  if (!amount) {
    return formatter.format(Number(0)).replace('$', '');
  }
  const num = amount.toString().replace(/,/g, '');

  return formatter.format(Number(num)).replace('$', '');
};

export const getDateObject = (date: string) => {
  if (!date) {
    return {
      month: '',
      day: '',
      year: '',
    };
  }
  return {
    month: parseInt(date.slice(5, 7)),
    day: parseInt(date.slice(8, 10)),
    year: date.slice(0, 4),
  };
};

export const getCurrentYear = () => {
  const dt = new Date();
  return dt.getFullYear();
};

export const getFormattedCurrentDate = () => {
  const dt = new Date();
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

export const formatDate = (date: any) => {
  const { month, day, year } = date;
  return `${month}/${day}/${year}`;
};

export const getDateString = (date: any) => {
  const { month, day, year } = date;
  return `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}T00:00:00.000+00:00`;
};

export const getIntegerAmount = (amount: number | string) => {
  const num = (amount + '').replace(/,/g, '');
  if (num === '') {
    return 0;
  }
  return parseInt(num);
};

export const getFloatAmount = (amount: number | string) => {
  const num = (amount + '').replace(/,/g, '');
  return parseFloat(num);
};

export const isNumeric = (num: string | number) => {
  return /^[0-9\b]*$/.test(num + '');
};

export const formatDollarAmountChange = (num: any) => {
  let cleanedNum = num.replace(/[$,.]/g, '');
  if (!isNumeric(cleanedNum)) {
    return '';
  }
  const numArr = cleanedNum.split('');
  if (numArr.length < 3) {
    numArr.unshift('0');
  } else {
    if (numArr[0] === '0') {
      numArr.shift();
    }
  }
  cleanedNum = numArr.join('');
  const formattedNumberWithDecimal =
    cleanedNum.slice(0, cleanedNum.length - 2) + '.' + cleanedNum.slice(cleanedNum.length - 2);
  return formattedNumberWithDecimal;
};

export const isAlpha = (str: string) => {
  return /^[a-zA-Z]*$/.test(str);
};

export const isWithinAllowedRange = (num: number | string, min: number, max: number, isFloat = false) => {
  const numericValue = isFloat ? getFloatAmount(num) : getIntegerAmount(num);
  return numericValue >= min && numericValue <= max;
};

export const containsAllowedNumberOfChars = (str: string, min: number, max: number) => {
  return str.length >= min && str.length <= max;
};

export const isValidBirthDate = ({ month, day, year }: any) => {
  const minMonth = 1,
    maxMonth = 12,
    minDate = 1,
    minYear = 1900,
    maxYear = getCurrentYear();
  const monthDateMap: Record<number, number> = {
    1: 31,
    2: (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };
  if (month < minMonth || month > maxMonth) {
    return false;
  }
  if (year < minYear || year > maxYear) {
    return false;
  }
  if (day < minDate || day > monthDateMap[month]) {
    return false;
  }
  return true;
};

export const isValidName = (name: string) => {
  return /^[a-zA-Z,.'-\s\b]*$/.test(name);
};

export const isValidAccountName = (name: string) => {
  return /^[0-9a-zA-Z,.'-\s\b]*$/.test(name);
};

export const getAge = (birthDate: string) => {
  if (!birthDate) {
    return 0;
  }
  return getCurrentYear() - Number(birthDate.slice(0, 4));
};

export const changeCase = (str: string) => {
  if (!str) {
    return '';
  }
  return str
    .split('_')
    .map((word: string) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

export const keyDown = (e: React.KeyboardEvent<HTMLDivElement>, onClickEvent: Function): void => {
  const code: string = e.key;
  if (code === 'Enter' || code === ' ') {
    e.preventDefault();
    return onClickEvent();
  }
};

export const getPreciseValue = (num: number) => {
  return Number(num.toFixed(0));
};

export const annualERContTooltip = () => {
  return `<div
      id="annual-savings-tooltip"
      role="tooltip"
      style=" width: 170px;height: 100px;font-family: Nimbus Sans Regular;font-size: 16px;padding: 8px 0px 8px 0px;"
    >
    Amount you expect the account owner's employer to contribute to this account each year.
    </div>`;
};

export const annualEEContTooltip = () => {
  return `<div
      id="annual-savings-tooltip"
      role="tooltip"
      style=" width: 140px;height: 100px;font-family: Nimbus Sans Regular;font-size: 16px;padding: 8px 0px 8px 0px;"
    >
    Amount you expect the account owner to contribute to this account each year.
    </div>`;
};

export const isEligbleForEEContribution = (contributionType: string) => {
  return contributionType === contributionTypes.EE || contributionType === contributionTypes.BOTH;
};

export const isEligbleForERContribution = (contributionType: string) => {
  return contributionType === contributionTypes.ER || contributionType === contributionTypes.BOTH;
};
